// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  performLogout, 
  fetchUserDetails, 
  getAccessToken, 
  getRefreshToken, 
  getUserId, 
  getSessionId,
  login as authServiceLogin,
  performTokenRefresh,
  storeAuthData,
  saveAdminSession,
  restoreAdminSession,
  isImpersonationActive,
  clearImpersonationData,
  fetchWithAuth
} from '../services/AuthService';
import { AuthApi, DATA_SERVICE_BASE_URL } from '../services/ApiService';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
// Subscription retrieval removed from AuthContext. Use SubscriptionContext instead.

const AuthContext = createContext();

let authInitializePromise = null;

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [user, setUser] = useState(null);
  // Active organization memberships for the current user (loaded after login / on init).
  // Shape: [{ organizationId: number, orgRole: 'org_admin'|'course_admin'|'org_member', status: 'active' }]
  const [orgMemberships, setOrgMemberships] = useState([]);
  // Subscription-related state removed; now centralized in SubscriptionContext.
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(() => isImpersonationActive());
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  // ✅ NEW: DataContext reference for cached validation
  const [dataContextRef, setDataContextRef] = useState(null);

  const logoutInProgress = useRef(false);
  const initializationInProgress = useRef(false);
  const tokenRefreshInProgress = useRef(false);

  // ✅ NEW: Set DataContext reference
  const setDataContext = useCallback((dataContext) => {
    setDataContextRef(dataContext);
  }, []);

  // ✅ CONSOLIDATED: Wrapper around unified performTokenRefresh (storage already updated there)
  const refreshAccessToken = useCallback(async () => {
    if (tokenRefreshInProgress.current) {
      return null;
    }
    tokenRefreshInProgress.current = true;
    try {
      const newToken = await performTokenRefresh();
      if (!newToken) return null;
      // Sync React state from storage (authoritative persisted values)
      const latestAccess = getAccessToken();
      const latestRefresh = getRefreshToken();
      setAccessToken(latestAccess);
      if (latestRefresh) setRefreshToken(latestRefresh);
      if (dataContextRef?.clearCache) dataContextRef.clearCache('validate-token');
      return newToken;
    } finally {
      tokenRefreshInProgress.current = false;
    }
  }, [dataContextRef]);

  // Fetch the user's active organization memberships from the backend.
  // Safe no-op when not authenticated; never throws (logs and returns []).
  const loadOrgMemberships = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        setOrgMemberships([]);
        return [];
      }
      const resp = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/me/memberships`, { method: 'GET' });
      if (!resp || !resp.ok) {
        if (resp && resp.status !== 401 && resp.status !== 403) {
          console.warn('AuthContext: loadOrgMemberships non-OK', resp.status);
        }
        return [];
      }
      const data = await resp.json().catch(() => []);
      const list = Array.isArray(data) ? data : [];
      setOrgMemberships(list);
      return list;
    } catch (e) {
      console.warn('AuthContext: loadOrgMemberships error', e?.message || e);
      return [];
    }
  }, []);

  // ✅ FIXED: Define checkTokenExpiration with proper dependencies
  const checkTokenExpiration = useCallback(async () => {
    const expirationTime = localStorage.getItem('token_expires_at');
    if (expirationTime && Date.now() > parseInt(expirationTime)) {
      console.log('AuthContext: Token expired, attempting refresh');
      await refreshAccessToken();
    }
  }, [refreshAccessToken]);

  // Replace validateTokenCached implementation
  const validateTokenCached = useCallback(async () => {
    const runDirect = async () => {
      const response = await AuthApi.validateToken();
      if (response.ok) return await response.json();
      if (response.status === 401 || response.status === 403) {
        throw new Error(`AUTH_${response.status}`);
      }
      throw new Error(`SERVER_${response.status}`);
    };

    if (dataContextRef?.validateToken) {
      try {
        return await dataContextRef.validateToken();
      } catch (e) {
        console.warn('AuthContext: Cached validation failed, falling back:', e.message);
        return await runDirect();
      }
    }
    return await runDirect();
  }, [dataContextRef]);

  // Update login to accept rememberMe and pass along
  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      
  // Use the AuthService login function directly
      const data = await authServiceLogin(email, password, rememberMe);
      
      // ✅ UPDATED: Handle JWT token response format (userId now extracted from token)
  const newAccessToken = data.accessToken;
  const newRefreshToken = data.refreshToken;
  const expiresIn = data.expiresIn || 1800; // Default 30 minutes
  const extractedUserId = data.userId; // userId extracted from JWT token
      
      // Store tokens and expiration
  setAccessToken(newAccessToken);
  setRefreshToken(newRefreshToken);
  setUserId(extractedUserId);
  setSessionId(data.sessionId);
  // Subscription handling moved to SubscriptionContext.
      const expirationTime = Date.now() + (expiresIn * 1000) - 60000; // Refresh 1 minute before expiry
      localStorage.setItem('token_expires_at', expirationTime.toString());
      
      // Persist rememberMe flag (for UI reuse)
      localStorage.setItem('remember_me_flag', String(!!rememberMe));
      
      // Fetch user details if not included in login response
      let resolvedUser = data.user || null;
      if (data.user) {
        setUser(data.user);
      } else {
        try {
          const userDetails = await fetchUserDetails();
          if (userDetails) {
            setUser(userDetails);
            resolvedUser = userDetails;
          }
        } catch (userError) {
          console.error('AuthContext: Error fetching user details after login:', userError);
        }
      }

      // Load organization memberships — awaited so callers (e.g. LoginPage)
      // can make role-aware redirect decisions immediately after login.
      let memberships = [];
      try {
        memberships = await loadOrgMemberships() || [];
      } catch (_) { /* non-fatal */ }

      // Track successful login
      try {
        UsageEventsTracker.recordUserAction('LOGIN_SUCCESS', {
          action: 'complete',
          category: 'auth',
          entityType: 'session',
          entityId: data.sessionId,
          label: 'Login Success',
          metadata: {
            email,
            rememberMe,
            userId: extractedUserId
          }
        }, { userId: extractedUserId });
      } catch (trackError) {
        console.warn('AuthContext: Failed to track login success', trackError);
      }
      
      return { success: true, user: resolvedUser, userId: extractedUserId, orgMemberships: memberships };
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      return { success: false, message: error.message || 'Login failed. Please try again.' };
    }
  }, [loadOrgMemberships]);

  /**
   * Start impersonating a target user.
   * Saves admin tokens, sets impersonated user tokens, reloads user profile.
   * @param {object} impersonationData - Response from POST /api/users/impersonate/{id}
   * @param {object} targetUserInfo - { userId, firstName, lastName, email } for banner display
   */
  const startImpersonation = useCallback(async (impersonationData, targetUserInfo) => {
    // 1. Save current admin session
    saveAdminSession();

    // 2. Store the impersonated user's tokens
    storeAuthData({
      accessToken: impersonationData.accessToken,
      refreshToken: impersonationData.refreshToken,
      expiresIn: impersonationData.expiresIn,
      userId: targetUserInfo.userId,
      sessionId: impersonationData.sessionId,
      rememberMe: false, // impersonation sessions never use remember-me
    });

    // 3. Update React state
    setAccessToken(impersonationData.accessToken);
    setRefreshToken(impersonationData.refreshToken);
    setUserId(targetUserInfo.userId);
    setSessionId(impersonationData.sessionId);
    setIsImpersonating(true);
    setImpersonatedUser(targetUserInfo);

    // 4. Reload user profile (now returns the impersonated user)
    try {
      const userData = await validateTokenCached();
      setUser(userData);
    } catch (e) {
      console.warn('AuthContext: Failed to load impersonated user profile', e);
    }

    // 5. Clear DataContext caches so the UI refreshes
    if (dataContextRef?.clearCache) {
      dataContextRef.clearCache('validate-token');
    }
  }, [validateTokenCached, dataContextRef]);

  /**
   * Stop impersonation and restore the admin's original session.
   */
  const stopImpersonation = useCallback(async () => {
    // 1. Restore the admin tokens from sessionStorage
    restoreAdminSession();

    // 2. Re-read stored values
    const restoredToken = getAccessToken();
    const restoredUserId = getUserId();
    const restoredSessionId = getSessionId();
    const restoredRefreshToken = getRefreshToken();

    // 3. Update React state
    setAccessToken(restoredToken);
    setRefreshToken(restoredRefreshToken);
    setUserId(restoredUserId);
    setSessionId(restoredSessionId);
    setIsImpersonating(false);
    setImpersonatedUser(null);

    // 4. Reload admin user profile
    if (dataContextRef?.clearCache) {
      dataContextRef.clearCache('validate-token');
    }
    try {
      const userData = await validateTokenCached();
      setUser(userData);
    } catch (e) {
      console.warn('AuthContext: Failed to reload admin profile after stopping impersonation', e);
    }
  }, [validateTokenCached, dataContextRef]);

  // ✅ UPDATED: Use userId for logout instead of sessionId
  const logout = useCallback(async () => {
    if (logoutInProgress.current) {
      console.log('Logout already in progress, skipping...');
      return;
    }
    
    logoutInProgress.current = true;
    
    try {
      
      // Flush usage events before logout to ensure delivery
      try {
        await UsageEventsTracker.flush({ reason: 'logout', keepalive: true });
      } catch (flushError) {
        console.warn('AuthContext: Failed to flush usage events on logout', flushError);
      }
      
      // ✅ UPDATED: Use userId instead of sessionId for logout
      if (user?.userId || user?.id || userId) {
        const userIdToLogout = user?.userId || user?.id || userId;
        await AuthApi.logout(userIdToLogout);
      }
      
      // ✅ UPDATED: Clear auth-related and health caches
      if (dataContextRef?.clearCache) {
        dataContextRef.clearCache('validate-token');
        dataContextRef.clearCache('health-public');
        dataContextRef.clearCache('health-auth');
      }
      
      // Clear state
      setAccessToken(null);
      setRefreshToken(null);
      setUserId(null);
      setSessionId(null);
      setUser(null);
      setOrgMemberships([]);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      
      // Clear impersonation data if active
      clearImpersonationData();
      
      // ✅ NEW: Clear token expiration
      localStorage.removeItem('token_expires_at');
      localStorage.removeItem('refresh_token_issued_at');
      localStorage.removeItem('remember_me_flag');
      
      // Clear from storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_id');
      localStorage.removeItem('session_id');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('refresh_token'); // ✅ FIX: Clear from sessionStorage too
      
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
    } finally {
      logoutInProgress.current = false;
    }
  }, [user, userId, dataContextRef]);

  const loadUser = useCallback(async () => {
    try {
      const currentUserId = getUserId();
      const currentToken = getAccessToken();
      
      if (!currentUserId || !currentToken) {
        return;
      }
      
      await checkTokenExpiration();
      
      // ✅ UPDATED: Use cached validation
      let userData = await validateTokenCached();
      // Fallback: if validate-token didn't return roles (lazy-load issue),
      // fetch full user details from /api/users/{id} which loads roles eagerly
      if (userData && (!userData.roles || userData.roles.length === 0)) {
        try {
          const fullUser = await fetchUserDetails();
          if (fullUser?.roles?.length > 0) {
            userData = { ...userData, roles: fullUser.roles };
          }
        } catch (e) {
          console.warn('AuthContext: roles fallback failed', e);
        }
      }
      setUser(userData);
      // Subscription refresh handled by SubscriptionContext.
      return userData;
    } catch (error) {
      console.warn('AuthContext: Error loading user:', error);
      if (error.message?.includes('AUTH_401') || error.message?.includes('AUTH_403')) {
        await logout();
      } else {
        console.warn('AuthContext: loadUser non-auth error suppressed:', error.message);
      }
    }
  }, [checkTokenExpiration, validateTokenCached, logout]);

  // ✅ NEW: Set up token refresh timer
  useEffect(() => {
    if (accessToken && refreshToken) {
      const checkInterval = setInterval(checkTokenExpiration, 5 * 60 * 1000); // Check every 5 minutes
      return () => clearInterval(checkInterval);
    }
  }, [accessToken, refreshToken, checkTokenExpiration]);

  // Listen for session-expired events dispatched by fetchWithAuth (avoids hard window.location redirect)
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn('AuthContext: Received auth:session-expired event, clearing state');
      // Clear React state without calling server logout (tokens are already invalid)
      setAccessToken(null);
      setRefreshToken(null);
      setUserId(null);
      setSessionId(null);
      setUser(null);
      setOrgMemberships([]);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      clearImpersonationData();
      if (dataContextRef?.clearCache) {
        dataContextRef.clearCache('validate-token');
      }
      logoutInProgress.current = false;
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [dataContextRef]);

  // ✅ UPDATED: Initialize with cached validation
  useEffect(() => {
    if (isInitialized || initializationInProgress.current) {
      return;
    }
    initializationInProgress.current = true;
    if (!authInitializePromise) {
      authInitializePromise = (async () => {
        try {
          const storedToken = getAccessToken();
          const storedUserId = getUserId();
          const storedSessionId = getSessionId();
          const storedRefreshToken = getRefreshToken();
          if (storedToken && storedUserId) {
            try {
              await checkTokenExpiration();
              const currentToken = getAccessToken();
              if (currentToken) {
                let userData = await validateTokenCached();
                // Fallback: if validate-token didn't return roles (lazy-load issue),
                // fetch full user details from /api/users/{id} which loads roles eagerly
                if (userData && (!userData.roles || userData.roles.length === 0)) {
                  try {
                    const fullUser = await fetchUserDetails();
                    if (fullUser?.roles?.length > 0) {
                      userData = { ...userData, roles: fullUser.roles };
                    }
                  } catch (e) {
                    console.warn('AuthContext: roles fallback during init failed', e);
                  }
                }
                setAccessToken(currentToken);
                setUserId(storedUserId);
                setSessionId(storedSessionId);
                setRefreshToken(getRefreshToken());
                setUser(userData);
                // Load org memberships for this restored session
                loadOrgMemberships().catch(() => {});
              }
            } catch (e) {
              if (String(e.message).startsWith('AUTH_')) {
                console.warn('AuthContext: Auth error during init, logging out');
                await performLogout();
              } else {
                console.warn('AuthContext: Server error during init (proceeding with stored tokens):', e.message);
                // Hydrate state so user is not forced out; defer user details load
                setAccessToken(storedToken);
                setUserId(storedUserId);
                setSessionId(storedSessionId);
                setRefreshToken(storedRefreshToken);
                // Schedule a silent retry to load user data
                setTimeout(() => {
                  validateTokenCached()
                    .then(u => { if (u) setUser(u); })
                    .catch(err => console.warn('AuthContext: Deferred validate retry failed:', err.message));
                }, 10000);
              }
            }
          }
        } finally {
          setIsInitialized(true);
          initializationInProgress.current = false;
        }
      })();
    }
  }, [isInitialized, checkTokenExpiration, validateTokenCached, loadOrgMemberships]); // functions are stable via useCallback

  // Memoize org-role helpers so consumers receive stable references and
  // don't re-render every time an unrelated AuthContext field changes.
  // Each callback only re-creates when the underlying memberships change.
  const getOrgRole = useCallback((organizationId) => {
    if (organizationId == null) return null;
    const id = Number(organizationId);
    const m = (orgMemberships || []).find(x => Number(x.organizationId) === id && x.status === 'active');
    return m ? m.orgRole : null;
  }, [orgMemberships]);

  const isOrgAdmin = useCallback((organizationId) => {
    if (organizationId == null) return false;
    const id = Number(organizationId);
    return (orgMemberships || []).some(m => Number(m.organizationId) === id && m.status === 'active' && m.orgRole === 'org_admin');
  }, [orgMemberships]);

  const isCourseAdminInOrg = useCallback((organizationId) => {
    if (organizationId == null) return false;
    const id = Number(organizationId);
    return (orgMemberships || []).some(m =>
      Number(m.organizationId) === id && m.status === 'active' && (m.orgRole === 'org_admin' || m.orgRole === 'course_admin'));
  }, [orgMemberships]);

  const hasAnyOrgAdminRole = useCallback(() => (orgMemberships || []).some(m =>
    m.status === 'active' && (m.orgRole === 'org_admin' || m.orgRole === 'course_admin')),
    [orgMemberships]);

  // True when the current user holds the global "parent" role. Roles come from
  // user.roles (each entry may be a {roleName} object or a bare string).
  const isParent = useCallback(() => (user?.roles || []).some(r =>
    String(r?.roleName ?? r?.name ?? r).toLowerCase() === 'parent'),
    [user]);

  // Returns the list of organizationIds where the user has an active admin role
  // (either org_admin or course_admin).
  const getAdminOrgIds = useCallback(() => (orgMemberships || [])
    .filter(m => m.status === 'active' && (m.orgRole === 'org_admin' || m.orgRole === 'course_admin'))
    .map(m => Number(m.organizationId)),
    [orgMemberships]);

  const value = {
    accessToken,
    refreshToken,
    userId,
    sessionId,
    user,
    orgMemberships,
    loadOrgMemberships,
    getOrgRole,
    isOrgAdmin,
    isCourseAdminInOrg,
    hasAnyOrgAdminRole,
    getAdminOrgIds,
    isParent,
  // subscription & paymentStatus removed; use SubscriptionContext
    isInitialized,
    login,
    logout,
    loadUser,
    refreshAccessToken,
    checkTokenExpiration,
    performLogout,
    setDataContext, // ✅ NEW: Expose DataContext setter
    // Impersonation
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    /**
     * Update user profile (first/last name). Assumes authenticated user.
     * @param {{firstName?:string,lastName?:string}} payload
     */
    updateUserProfile: async (payload = {}) => {
      try {
        const id = user?.userId || user?.id || userId;
        if (!id) throw new Error('No user id');
        const body = { ...payload };
  const resp = await fetch(`${DATA_SERVICE_BASE_URL}/api/users/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
          body: JSON.stringify(body),
          credentials: 'include'
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Profile update failed (${resp.status})`);
        }
        const updated = await resp.json().catch(()=>body);
        setUser(u => ({ ...u, ...updated }));
        return updated;
      } catch (e) {
        console.error('AuthContext:updateUserProfile error', e);
        throw e;
      }
    },
    /**
     * Change password for logged in user using current + new password.
     * Backend endpoint assumed: POST /api/users/change-password (adjust if different).
     * @param {{currentPassword:string,newPassword:string}} params
     */
    changePassword: async ({ currentPassword, newPassword }) => {
      if (!currentPassword || !newPassword) throw new Error('Missing passwords');
      if (!accessToken) throw new Error('Not authenticated');
      try {
  const url = `${DATA_SERVICE_BASE_URL}/api/users/change-password`;
        // Backend DTO expects { accessToken, currentPassword, newPassword }
        const payload = { accessToken, currentPassword, newPassword };
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(payload),
          credentials: 'include'
        });
        if (!resp.ok) {
          let message = `Password update failed (${resp.status})`;
          try {
            if ((resp.headers.get('content-type')||'').includes('application/json')) {
              const data = await resp.json();
              message = data?.message || data?.error || message;
            } else {
              const txt = await resp.text();
              message = txt || message;
            }
          } catch(_){}
          throw new Error(message);
        }
        return true;
      } catch (e) {
        console.error('AuthContext:changePassword error', e);
        throw e;
      }
    }
  };

  // Removed legacy subscription fetch/refresh; handled by SubscriptionContext.


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // Graceful fallback: instead of throwing (which crashes entire subtree when a component
    // is accidentally mounted outside the provider), return a non-initialized stub.
    // This preserves existing component defensive checks (many already gate on user/isInitialized)
    // and emits a single console warning so the underlying mounting issue can be traced.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[AuthContext] useAuth invoked with no provider above in tree. Returning fallback stub.');
    }
    return {
      accessToken: null,
      refreshToken: null,
      userId: null,
      sessionId: null,
      user: null,
      isInitialized: false,
      login: async () => ({ success: false, message: 'AuthProvider missing' }),
      logout: async () => {},
      loadUser: async () => null,
      refreshAccessToken: async () => null,
      checkTokenExpiration: async () => {},
      performLogout: async () => {},
      setDataContext: () => {},
      isImpersonating: false,
      impersonatedUser: null,
      startImpersonation: async () => {},
      stopImpersonation: async () => {},
      updateUserProfile: async () => { throw new Error('AuthProvider missing'); },
      changePassword: async () => { throw new Error('AuthProvider missing'); }
    };
  }
  return context;
}
